import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useParams, useHistory, Link, useLocation } from "react-router-dom";
import { BsCheckCircle } from "react-icons/bs";
import moment from "moment";
import { fetchEventDetails } from "../../../state/events/actions";
import eventType from "../../../prop-types/eventType";
import {
  USER,
  PLAYER,
  EVENT,
  SCHEDULED,
  PLANNED,
  ON_DEMAND,
  LIVE,
  VIDEO,
  ACCOUNT,
  ORDER_HISTORY
} from "../../../constants";
import { Template } from "../../../commons/elements/index";
import "../styles/product-order-confirmation.scss";

const propTypes = {
  types: PropTypes.arrayOf(eventType).isRequired,
  // Map state to props
  selected: PropTypes.object.isRequired,
  // Map dispatch to props
  fetchEventDetailsAction: PropTypes.func.isRequired
};

export function ProductOrderConfirmation(props) {
  const { selected, fetchEventDetailsAction, types } = props;
  const [countdown, setCountdown] = useState(5);
  const [isThirdParty, setIsThirdParty] = useState(false);
  const params = useParams();
  const history = useHistory();
  const location = useLocation();

  const canRedirect =
    selected &&
    selected.id &&
    (selected.data.status === ON_DEMAND || selected.data.status === LIVE);

  useEffect(() => {
    fetchEventDetailsAction({ id: params.eventId, streamType: VIDEO });

    const countdownInterval = setInterval(() => {
      // Decrease the countdown value every second
      setCountdown((prevCountdown) => prevCountdown - 1);
    }, 1000);

    // Cleanup function to clear the interval when the component unmounts
    return () => clearInterval(countdownInterval);
  }, []);

  useEffect(() => {
    let timeoutId = null;
    // Use setTimeout to reset the countdown after it reaches 0
    if (countdown === 0) {
      timeoutId = setTimeout(() => {
        setCountdown(5); // Reset the countdown to 5 seconds
      }, 2000); // Delay before resetting (2 seconds)
    }

    if (countdown === 0 && canRedirect && !isThirdParty) {
      history.push(
        `/${params.shortName}/${PLAYER}/${EVENT}/${params.eventId}/${selected.data.streamType}`
      );
    }

    // Cleanup function to clear the timeout if the component unmounts
    return () => clearTimeout(timeoutId);
  }, [canRedirect, countdown]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setIsThirdParty(searchParams.get("isThirdParty") === "true");
  }, [location]);

  const isPlanned = [SCHEDULED, PLANNED].includes(selected.data.status);

  return (
    <Template tabName={USER} types={types}>
      <div
        role="region"
        className="product-order-confirmation-container d-flex flex-column container-fluid px-0"
      >
        <div className="d-flex flex-column product-order-confirmation">
          <div className="order-confirmation-message m-3">
            <BsCheckCircle />
            <span className="ml-2">Order Confirmation</span>
          </div>
          <div className="passport-message-container m-3">
            <span>
              {!isThirdParty
                ? "Your purchase was successful!"
                : "Thank you for your purchase."}
            </span>
          </div>
          {!isThirdParty && canRedirect && countdown > 0 && (
            <div className="redirect-message-container m-3">
              <span className="redirect-message">
                You will be redirected in {countdown}{" "}
                {countdown > 1 ? "seconds" : "second"}
              </span>
            </div>
          )}
          {isThirdParty && (
            <div className="redirect-message-container m-3 d-flex flex-column">
              <div className="redirect-message mb-3">
                You can now close the browser and view the video your mobile or
                OTT device.
              </div>
            </div>
          )}
          {selected && selected.id && isPlanned && (
            <div className="event-start-container m-3">
              <span className="event-start-message">
                {`This event will begin at ${moment(
                  selected.data.startTime
                ).format("LT")} ${moment(selected.data.startTime).format(
                  "[ on ]L"
                )}`}
              </span>
            </div>
          )}
          {!isThirdParty && (
            <div className="view-orders-link-container m-3">
              <Link to={`/${params.shortName}/${ACCOUNT}/${ORDER_HISTORY}`}>
                View your purchases
              </Link>
            </div>
          )}
        </div>
      </div>
    </Template>
  );
}

ProductOrderConfirmation.propTypes = propTypes;

const mapStateToProps = (state) => ({
  selected: state.events.events.selected || {}
});

const mapDispatchToProps = {
  fetchEventDetailsAction: fetchEventDetails
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProductOrderConfirmation);
